import React, {Component} from 'react';
import {connect} from 'react-redux';
import actions from './profile.actions';
import ProfileCard, {PresentationProfileCard} from './ProfileCard';
import {DocumentTitle, EmptyMessage, Spinner, TaxonomyList, PrimaryButton} from '../widgets';
import './ProfileFavouritesPage.css';
import {createSelector} from 'reselect';
import {uiActions} from '../chrome';
import {checkOnInit, checkOnPropChanges} from './profilePathCheck';
import {getShareProfileFacebook, getShareProfileLinkedin} from './sharing.util';
import Icon from '../widgets/Icon';
import FancyButton from '../widgets/FancyButton';

const groupByCategory = createSelector(
	props => props.profile.favourites,
	providers => {
		if (!providers) return providers;

		return providers.reduce((accum, provider) => {
			accum[provider.category] = accum[provider.category] || [];
			accum[provider.category].push(provider);
			return accum;
		}, {});
	}
);

export const getSortedCategories = createSelector(
	groupByCategory,
	byCategory => byCategory && Object.keys(byCategory).sort()
);

class ProfileFavourites extends Component {
	constructor(props) {
		super(props);

		checkOnInit(props);
	}

	componentWillReceiveProps(nextProps) {
		checkOnPropChanges(nextProps, this.props);
	}

	render() {
		if (
			!this.props ||
			!this.props.profile ||
			!this.props.profile.user ||
			!this.props.profile.profile ||
			!this.props.profile.favourites
		) {
			return (
				<DocumentTitle title="B2Me">
					<main className="ProfileFavouritesPage">
						<Spinner />
					</main>
				</DocumentTitle>
			);
		}

		const {
			profile: {
				uuid,
				profession,
				user: {firstname, lastname, uuid: userUuid, location},
				profile: {backgroundPosition = 33, backgroundImage, avatarImage, uniqueName, brief},
				editingField,
				updatingAvatarImage,
				updatingBackgroundImage,
				category,
				subscriptionLevel
			},
			sessionProvider,
			showInvite
		} = this.props;

		const isViewingOwn =
			userUuid === (sessionProvider && sessionProvider.user && sessionProvider.user.uuid);
		const isLoggedIn = !!(sessionProvider && sessionProvider.user);

		if (!this.props.profile.favourites.length) {
			return (
				<DocumentTitle title="B2Me">
					<main className="ProfileFavouritesPage">
						<div className="ProfileFavouritesPage-aside">
							{isViewingOwn && (
								<PrimaryButton onClick={showInvite} icon="insert_invitation">
									Invite someone to B2Me
								</PrimaryButton>
							)}
						</div>
						<article className="ProfileFavouritesPage-favourites">
							<EmptyMessage
								extraClass={isViewingOwn ? 'ProfileFavouritesPage-favourites-own-message' : ''}>
								{isViewingOwn ? (
									<div>
										<p>You haven't added anyone to your collection yet</p>
										<p className="EmptyMessage-subtext">
											Collect your favourite providers for easy access and to share them with
											friends.
										</p>
									</div>
								) : (
									`${firstname || 'This provider'} hasn't added anyone to their collection yet`
								)}
							</EmptyMessage>
							{isViewingOwn && <ExampleFavourite sessionProvider={sessionProvider} />}
						</article>
					</main>
				</DocumentTitle>
			);
		}

		const byCategory = groupByCategory(this.props);
		const sortedCategories = getSortedCategories(this.props);

		return (
			<DocumentTitle
				title={`${this.props.profile.user.firstname} ${this.props.profile.user.lastname} - B2Me`}>
				<main className="ProfileFavouritesPage">
					<section className="ProfileFavouritesPage-aside">
						{isViewingOwn && (
							<PrimaryButton onClick={showInvite} icon="insert_invitation">
								Invite someone to B2Me
							</PrimaryButton>
						)}
						{isLoggedIn && (
							<FancyButton onClick={getShareProfileFacebook(uniqueName)} icon="facebook2">
								<Icon name="facebook2" />
								Share on Facebook
							</FancyButton>
						)}
						{isLoggedIn && (
							<FancyButton
								onClick={getShareProfileLinkedin(
									uniqueName,
									firstname,
									lastname,
									brief,
									'collections'
								)}
								icon="linkedin">
								<Icon name="linkedin" />
								Share on LinkedIn
							</FancyButton>
						)}
						<TaxonomyList className="ProfileFavouritesPage-taxonomies">
							{sortedCategories &&
								sortedCategories.map((categoryName, idx) => (
									<li key={categoryName}>
										<a
											onClick={() => {
												const el = document.getElementById(`cat${idx + 1}`);
												el.scrollIntoView({
													behavior: 'smooth',
													block: 'center'
												});
											}}>
											{categoryName}
										</a>
									</li>
								))}
						</TaxonomyList>
					</section>
					<article className="ProfileFavouritesPage-favourites">
						{sortedCategories &&
							sortedCategories.map((categoryName, idx) => (
								<div key={categoryName} className="ProfileFavouritesPage-category-list">
									<h3 key={categoryName}>{categoryName}</h3>
									<ol id={`cat${idx + 1}`}>
										{byCategory[categoryName].map(provider => (
											<ProfileCard key={provider.uuid} provider={provider} />
										))}
									</ol>
								</div>
							))}
					</article>
				</main>
			</DocumentTitle>
		);
	}
}

class ExampleFavourite extends Component {
	constructor() {
		super();

		this.state = {
			favourite: false
		};

		this.setFavourite = (uuid, isFavourite) => {
			this.setState({favourite: isFavourite});
		};
	}

	render() {
		const {sessionProvider} = this.props;

		return (
			<div className="ProfileFavouritesPage-ExampleFavourite">
				<ol>
					<PresentationProfileCard
						disabled={true}
						sessionProvider={sessionProvider}
						setFavourite={this.setFavourite}
						confirmUnfavourite={(name, callback) => callback()}
						favourites={{
							'3fc227ff-7e65-468e-9681-b39161b3594f': this.state.favourite
						}}
						provider={{
							uuid: '3fc227ff-7e65-468e-9681-b39161b3594f',
							user: {
								firstname: 'Example',
								lastname: 'Provider',
								location: {
									suburb: 'Perth'
								}
							},
							profile: {},
							available: true,
							subscriptionLevel: 'essentials'
						}}
					/>
					<li className="ProfileFavouritesPage-ExampleFavourite-blurb">
						{this.state.favourite ? (
							<p className="done">
								That's how it's done, Great job! <Icon size={200} name="favorite" />
							</p>
						) : (
							<p>
								Whenever you see a provider you like, click the{' '}
								<Icon size={200} name="favorite_border" /> heart icon to add them to your "My
								providers". Click on the heart on the example card for a demonstration.
							</p>
						)}
					</li>
				</ol>
			</div>
		);
	}
}

const ConnectedProfileFavourites = connect(
	({profile, reviews, session}) => ({
		profile,
		sessionProvider: session && session.current,
		// ISSUE: this is derived state. It doesn't really belong in mapStateToProps
		editable:
			session.current &&
			session.current.user &&
			profile &&
			(session.current.user.admin || session.current.provider.uuid === profile.uuid)
	}),
	dispatch => ({
		showInvite: () => dispatch(uiActions.uiModal('invite')),
		retrieveProfile: uniqueName => dispatch(actions.retrieveProfile(uniqueName))
	})
)(ProfileFavourites);

export default ConnectedProfileFavourites;



// WEBPACK FOOTER //
// ./src/profile/ProfileFavouritesPage.js